import { useState, type FC, type ReactNode } from "react";
import {
  ChevronDown,
  ChevronRight,
  FileCheck,
  FileText,
  Folder,
  ListChecks,
  Lock,
  Package,
  type LucideIcon
} from "lucide-react";

import type {
  ChecklistEntry,
  ChecklistInternalCase,
  ChecklistInternalItem,
  ChecklistSection as ChecklistSectionData
} from "@/api/types/foreignNationalDocument";
import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import {
  ChecklistEntryRow,
  ChecklistSlotRow,
  type ChecklistRowContext
} from "./ChecklistSlotRow";
import { sectionCount } from "./checklistModel";

const SectionLabel: FC<{ label: string; count: ReactNode }> = ({
  label,
  count
}) => (
  <div className="flex items-baseline gap-1.5 px-4 pt-3.5 pb-1.5">
    <span className="text-sm font-medium text-fg-2">{label}</span>
    <span className="text-sm text-fg-3 tabular-nums">{count}</span>
  </div>
);

/** One kind ("Identity 2"): full-bleed rows, divided, no card. */
export const ChecklistSection: FC<{
  section: ChecklistSectionData;
  ctx: ChecklistRowContext;
}> = ({ section, ctx }) => (
  <section data-testid={`checklist-section-${section.kind}`}>
    <SectionLabel label={section.title} count={sectionCount(section)} />
    <div className="divide-y divide-line-1 border-t border-line-1">
      {section.slots.map((slot) => (
        <ChecklistSlotRow key={slot.key} slot={slot} ctx={ctx} />
      ))}
    </div>
  </section>
);

/** Client documents whose type isn't set. */
export const ChecklistOtherSection: FC<{
  entries: ChecklistEntry[];
  ctx: ChecklistRowContext;
}> = ({ entries, ctx }) => (
  <section data-testid="checklist-section-other">
    <SectionLabel label="Other" count={entries.length} />
    <div className="divide-y divide-line-1 border-t border-line-1">
      {entries.map((entry) => (
        <ChecklistEntryRow
          key={entry.id}
          entry={entry}
          ctx={ctx}
          variant="other"
        />
      ))}
    </div>
  </section>
);

const INTERNAL_ICONS: Record<
  Exclude<ChecklistInternalItem["kind"], "file">,
  LucideIcon
> = {
  draft: FileText,
  form: FileCheck,
  packet: Package,
  plan: ListChecks
};

const InternalItemRow: FC<{
  item: ChecklistInternalItem;
  caseLabel?: string;
  /** Inside a case subfolder. */
  nested?: boolean;
  onOpen: (item: ChecklistInternalItem) => void;
}> = ({ item, caseLabel, nested, onOpen }) => {
  const Icon = item.kind === "file" ? null : INTERNAL_ICONS[item.kind];
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-3 py-2 pr-4 text-left hover:bg-fill-1",
        nested ? "pl-16" : "pl-10"
      )}
      onClick={() => onOpen(item)}
      data-testid={`checklist-internal-item-${item.id}`}
    >
      {Icon ? (
        <Icon className="size-4 shrink-0 text-fg-3" aria-hidden />
      ) : (
        <FileTypeIcon
          name={item.title}
          mimeType={item.mime_type ?? undefined}
          size={16}
        />
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-base font-medium text-fg">
          {item.title}
        </span>
        <span className="truncate text-sm text-fg-3">{item.subtitle}</span>
      </span>
      {caseLabel ? <Tag>{caseLabel}</Tag> : null}
    </button>
  );
};

const FolderToggle: FC<{
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  indent?: boolean;
  note?: string;
  testId: string;
}> = ({ label, count, open, onToggle, indent, note, testId }) => {
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-2 py-2 pr-4 text-left hover:bg-fill-1",
        indent ? "pl-10" : "pl-4"
      )}
      aria-expanded={open}
      onClick={onToggle}
      data-testid={testId}
    >
      <Chevron className="size-3.5 shrink-0 text-fg-3" aria-hidden />
      <Folder className="size-4 shrink-0 text-fg-3" aria-hidden />
      <span className="text-base font-medium text-fg-2">{label}</span>
      <span className="text-sm text-fg-3 tabular-nums">{count}</span>
      {note ? (
        <span className="ml-auto flex items-center gap-1 text-sm text-fg-3">
          <Lock className="size-3" aria-hidden />
          {note}
        </span>
      ) : null}
    </button>
  );
};

const InternalCaseFolder: FC<{
  group: ChecklistInternalCase;
  forceOpen: boolean;
  onOpen: (item: ChecklistInternalItem) => void;
}> = ({ group, forceOpen, onOpen }) => {
  const [open, setOpen] = useState(false);
  const shown = open || forceOpen;
  return (
    <div>
      <FolderToggle
        label={group.case_label}
        count={group.items.length}
        open={shown}
        onToggle={() => setOpen(!shown)}
        indent
        testId={`checklist-internal-case-${group.individual_id}`}
      />
      {shown ? (
        <div>
          {group.items.map((item) => (
            <InternalItemRow key={item.id} item={item} nested onOpen={onOpen} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

/**
 * The firm's own files for the client's cases, in one folder: drafts, filled
 * forms, packets, plans, and case files marked Internal or Work Product. A
 * subfolder per case when there are several. Never shown in the portal.
 */
export const ChecklistInternalSection: FC<{
  cases: ChecklistInternalCase[];
  /** A search is on: open every folder so matches show. */
  forceOpen?: boolean;
  onOpen: (item: ChecklistInternalItem) => void;
}> = ({ cases, forceOpen = false, onOpen }) => {
  const [open, setOpen] = useState(false);
  const shown = open || forceOpen;
  const count = cases.reduce((n, c) => n + c.items.length, 0);
  const single = cases.length === 1 ? cases[0] : null;
  return (
    <section
      className="border-t border-line-1"
      data-testid="checklist-section-internal"
    >
      <FolderToggle
        label="Internal"
        count={count}
        open={shown}
        onToggle={() => setOpen(!shown)}
        note="Only Your Team Sees This"
        testId="checklist-internal-toggle"
      />
      {shown ? (
        <div className="divide-y divide-line-1 border-t border-line-1">
          {single
            ? single.items.map((item) => (
                <InternalItemRow
                  key={item.id}
                  item={item}
                  caseLabel={single.case_label}
                  onOpen={onOpen}
                />
              ))
            : cases.map((group) => (
                <InternalCaseFolder
                  key={group.individual_id}
                  group={group}
                  forceOpen={forceOpen}
                  onOpen={onOpen}
                />
              ))}
        </div>
      ) : null}
    </section>
  );
};
